import type { DismissalState, UsePwaInstallReturn } from '@/app/pwa/types';
import { useModals } from '@/common/composables/useModals';
import { ModalIdentifierKey } from '@/common/types/modalIdentifierKey.enum';
import { useAuthStore } from '@/modules/auth/stores/auth.store';
import { computed, onMounted, ref } from 'vue';

/**
 * @author Omar Sánchez Martínez
 * @since 2025-11-19
 * @updated 2025-11-21
 * @description Composable for managing iOS PWA installation prompt
 * Detects iOS devices, checks standalone mode, and manages dismissal state with 30-day expiration
 * Now supports two-step flow: toast/banner first, then modal with instructions on demand
 *
 * @returns {UsePwaInstallReturn} Object containing iOS detection state and prompt management methods
 *
 * @example
 * const { showToast, showInstructions, dismissToast, dismissPermanently } = usePwaInstall();
 *
 * if (showToast.value) {
 *   // Display toast/banner
 * }
 */
export function usePwaInstall(): UsePwaInstallReturn {
  // Constants
  const TEMPORARY_DISMISSAL_KEY = 'munzen-pwa-toast-dismissed';
  const PERMANENT_DISMISSAL_KEY = 'munzen-pwa-toast-dismissed-permanent';
  const DISMISSAL_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

  // State
  const isIOS = ref(false);
  const isStandalone = ref(false);
  const isTemporarilyDismissed = ref(false);
  const isPermanentlyDismissed = ref(false);

  // Modal management
  const { openModal, isModalOpen } = useModals();
  const isInstructionsModalOpen = isModalOpen(ModalIdentifierKey.PWA_INSTALL_INSTRUCTIONS_MODAL);

  // Auth check
  const authStore = useAuthStore();

  /**
   * Detect if device is iOS
   * Checks user agent for iPad, iPhone, or iPod
   * Excludes Windows Phone devices
   */
  const detectIOS = (): boolean => {
    const userAgent = navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);
    // MSStream es una propiedad específica de Windows Phone que no está en la interfaz Window estándar
    const isNotWindowsPhone = !(window as Window & { MSStream?: boolean }).MSStream;
    return isIOSDevice && isNotWindowsPhone;
  };

  /**
   * Detect if app is running in standalone mode
   * Checks display-mode media query and navigator.standalone property
   */
  const detectStandalone = (): boolean => {
    // Check display-mode media query (standard)
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;

    // Check navigator.standalone property (iOS Safari)
    // standalone es una propiedad específica de Safari iOS que no está en la interfaz Navigator estándar
    const isNavigatorStandalone =
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

    return isStandaloneMode || isNavigatorStandalone;
  };

  /**
   * Check if dismissal has expired
   * Returns true if dismissal is older than 30 days
   */
  const isDismissalExpired = (dismissalState: DismissalState): boolean => {
    const now = Date.now();
    const dismissedTime = dismissalState.timestamp;
    const timeSinceDismissal = now - dismissedTime;
    return timeSinceDismissal > DISMISSAL_DURATION;
  };

  /**
   * Load temporary dismissal state from localStorage
   * Returns null if not found or expired
   */
  const loadTemporaryDismissalState = (): DismissalState | null => {
    try {
      const stored = localStorage.getItem(TEMPORARY_DISMISSAL_KEY);
      if (!stored) return null;

      const dismissalState: DismissalState = JSON.parse(stored);

      // Check if dismissal has expired
      if (isDismissalExpired(dismissalState)) {
        localStorage.removeItem(TEMPORARY_DISMISSAL_KEY);
        return null;
      }

      return dismissalState;
    } catch (error) {
      console.error('Error loading temporary dismissal state:', error);
      return null;
    }
  };

  /**
   * Load permanent dismissal state from localStorage
   * Returns null if not found
   */
  const loadPermanentDismissalState = (): DismissalState | null => {
    try {
      const stored = localStorage.getItem(PERMANENT_DISMISSAL_KEY);
      if (!stored) return null;

      const dismissalState: DismissalState = JSON.parse(stored);
      return dismissalState;
    } catch (error) {
      console.error('Error loading permanent dismissal state:', error);
      return null;
    }
  };

  /**
   * Save temporary dismissal state to localStorage
   */
  const saveTemporaryDismissalState = (): void => {
    try {
      const dismissalState: DismissalState = {
        dismissed: true,
        timestamp: Date.now(),
      };
      localStorage.setItem(TEMPORARY_DISMISSAL_KEY, JSON.stringify(dismissalState));
    } catch (error) {
      console.error('Error saving temporary dismissal state:', error);
    }
  };

  /**
   * Save permanent dismissal state to localStorage
   */
  const savePermanentDismissalState = (): void => {
    try {
      const dismissalState: DismissalState = {
        dismissed: true,
        timestamp: Date.now(),
      };
      localStorage.setItem(PERMANENT_DISMISSAL_KEY, JSON.stringify(dismissalState));
    } catch (error) {
      console.error('Error saving permanent dismissal state:', error);
    }
  };

  /**
   * Computed property to determine if toast should be shown
   * Shows if: User is authenticated AND iOS device AND not in standalone mode AND not dismissed (temporary or permanent)
   */
  const showToast = computed(() => {
    return (
      authStore.isAuthenticated &&
      isIOS.value &&
      !isStandalone.value &&
      !isTemporarilyDismissed.value &&
      !isPermanentlyDismissed.value
    );
  });

  /**
   * Dismiss the toast temporarily
   * Closes the toast for current session (30 days)
   */
  const dismissToast = (): void => {
    isTemporarilyDismissed.value = true;
    saveTemporaryDismissalState();
  };

  /**
   * Dismiss the toast permanently
   * User doesn't want to see it again
   */
  const dismissPermanently = (): void => {
    isPermanentlyDismissed.value = true;
    isTemporarilyDismissed.value = true;
    savePermanentDismissalState();
    // Also save temporary state to close the toast immediately
    saveTemporaryDismissalState();
  };

  /**
   * Show the instructions modal
   * Opens the modal with step-by-step installation instructions
   */
  const showInstructions = (): void => {
    openModal(ModalIdentifierKey.PWA_INSTALL_INSTRUCTIONS_MODAL);
  };

  /**
   * Initialize the composable
   * Detect iOS and standalone mode, check dismissal states
   */
  const initialize = (): void => {
    // Detect iOS
    isIOS.value = detectIOS();

    // Detect standalone mode
    isStandalone.value = detectStandalone();

    // Check permanent dismissal state first
    const permanentDismissalState = loadPermanentDismissalState();
    isPermanentlyDismissed.value =
      permanentDismissalState !== null && permanentDismissalState.dismissed;

    // Check temporary dismissal state
    const temporaryDismissalState = loadTemporaryDismissalState();
    isTemporarilyDismissed.value =
      temporaryDismissalState !== null && temporaryDismissalState.dismissed;
  };

  /**
   * Setup on component mount
   */
  onMounted(() => {
    initialize();
  });

  return {
    isIOS,
    isStandalone,
    showToast,
    isInstructionsModalOpen,
    dismissToast,
    dismissPermanently,
    showInstructions,
  };
}
